/**
 * The kit of the «с нуля» interface (Spec 022) — the parts the new screens are
 * built from, drawn after the round-2 mockups agreed by both agents and
 * approved by Alexander (27.09). Brand tokens only; touch targets ≥ 44 px; no
 * emoji for icons; a status is never colour alone.
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, UserRound } from "lucide-react";

import { isInTelegram } from "@/boot/telegram";
import { cn } from "@/lib/cn";

/** Whether a media query matches, kept current as the window changes. */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return;
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

/** ≥ 1024 px: a computer — lists and what is open side by side. */
export function useIsDesktop(): boolean {
  return useMedia("(min-width: 1024px)");
}

/**
 * A list and what is open from it, on a computer (PC audit 28.09, point 1).
 * From 1280 px they sit side by side. Below that there is room for one: the open
 * item takes the width with a way back to the list, instead of a 250 px strip.
 * With nothing open, the right column says what to do only when there is
 * something to pick (point 9). Side by side, each column scrolls on its own:
 * reading a long task no longer carries the list away (point 15).
 */
export function ListPane({ list, pane, open, onClose, placeholder, listWidth = 380 }: {
  list: ReactNode;
  pane: ReactNode;
  open: boolean;
  onClose: () => void;
  placeholder?: ReactNode;
  listWidth?: number;
}) {
  const wide = useMedia("(min-width: 1280px)");
  if (!wide) {
    if (!open) return <div className="max-w-[760px]">{list}</div>;
    return (
      <div className="flex max-w-[860px] flex-col gap-3">
        <button
          type="button"
          onClick={onClose}
          className="press-surface -ml-2 inline-flex min-h-11 items-center gap-1 self-start rounded-compact px-2 text-[15px] font-semibold text-p-text-muted hover:text-p-text"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          К списку
        </button>
        {pane}
      </div>
    );
  }
  return (
    // The page's own padding is 2rem on top and 3rem below (App): together they fill the screen.
    <div className="grid h-[calc(100dvh-5rem)] gap-8" style={{ gridTemplateColumns: `${listWidth}px minmax(0, 1fr)` }}>
      <div className="min-h-0 min-w-0 overflow-y-auto pb-6 pr-1 [scrollbar-gutter:stable]">{list}</div>
      <div className="min-h-0 min-w-0 overflow-y-auto border-l border-p-border pb-6 pl-8 [scrollbar-gutter:stable]">{open ? pane : placeholder ?? null}</div>
    </div>
  );
}

/** A screen's title: 26/32 bold and one quiet line under it. */
export function ScreenHeader({ title, sub, trailing }: { title: ReactNode; sub?: ReactNode; trailing?: ReactNode }) {
  return (
    <header className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[26px] font-bold leading-8 tracking-[-0.01em] text-p-text">{title}</h1>
        {sub && <p className="text-meta text-p-text-muted">{sub}</p>}
      </div>
      {trailing}
    </header>
  );
}

/**
 * The bar of a nested screen: back, where it belongs, and «···». Telegram has
 * its own Back — inside Telegram the chevron is not drawn.
 */
export function AppBar({ context, fallback, trailing, back = true }: { context?: ReactNode; fallback: string; trailing?: ReactNode; back?: boolean }) {
  const navigate = useNavigate();
  const tg = isInTelegram() || !back;
  return (
    <div className="flex min-h-11 items-center gap-1">
      {!tg && (
        <button
          type="button"
          aria-label="Назад"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(fallback))}
          className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full text-p-text press-surface"
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
      )}
      <span className="min-w-0 flex-1 truncate text-body font-semibold text-p-text-muted">{context}</span>
      {trailing}
    </div>
  );
}

/** The profile on a phone; on a computer it is in the sidebar. */
export function ProfileButton() {
  return (
    <Link to="/me" aria-label="Профиль" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-p-border bg-p-surface text-p-text press-surface lg:hidden">
      <UserRound className="h-5 w-5" aria-hidden="true" />
    </Link>
  );
}

/** «Кости», «Александра» — for «агент Кости». Plain rules; a name they miss stays as is. */
export function genitive(name: string): string {
  const n = name.trim();
  if (!n || /\s/.test(n)) return n;
  const last = n.slice(-1);
  if (last === "я") return n.slice(0, -1) + "и";
  if (last === "а") return n.slice(0, -1) + (/[гкхжшщч]$/.test(n.slice(0, -1)) ? "и" : "ы");
  if (last === "й" || last === "ь") return n.slice(0, -1) + "я";
  if (/[бвгдзклмнпрстфхцчшщж]$/.test(n)) return n + "а";
  return n;
}

/** «агент Кости» or «Костя» — who did it, never a token id. */
export function whoDid(name: string | null | undefined, viaAgent: boolean): string {
  const who = name || "участник";
  return viaAgent ? `агент ${genitive(who)}` : who;
}

export interface LastAction {
  who: string;
  what: string;
  at: string;
}

/**
 * «Сейчас по задаче» (Kostya, round 2): where the work stands — stage, next
 * step, obstacle, whose decision it needs — and who did what last. The whole
 * block opens the journal it comes from.
 */
export function NowLine({ now, next, blocker, needs, last, onOpen }: {
  now?: ReactNode;
  next?: ReactNode;
  blocker?: ReactNode;
  needs?: ReactNode;
  last?: LastAction | null;
  onOpen?: () => void;
}) {
  if (!now && !next && !blocker && !needs && !last) return null;
  const body = (
    <>
      <span aria-hidden="true" className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-p-text" />
      <span className="min-w-0 flex-1">
        {(now || next) && (
          <span className="block">
            {now && <><b className="font-bold">Сейчас:</b> {typeof now === "string" && next && !/[.!?…]$/.test(now) ? `${now}.` : now}</>}
            {now && next && " "}
            {next && <><b className="font-bold">Дальше:</b> {next}</>}
          </span>
        )}
        {blocker && <span className="block"><b className="font-bold">Препятствие:</b> {blocker}</span>}
        {needs && <span className="block"><b className="font-bold">Нужно</b> {needs}</span>}
        {last && <span className="mt-1 block text-meta text-p-text-muted">{last.who} · {ago(last.at)} · {last.what}</span>}
      </span>
      {onOpen && <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />}
    </>
  );
  const cls = "flex w-full gap-2 rounded-xl bg-p-now-tint px-3 py-2.5 text-left text-body text-p-text";
  return onOpen
    ? <button type="button" aria-label="Открыть журнал задачи" onClick={onOpen} className={cn(cls, "press-surface")}>{body}</button>
    : <div className={cls}>{body}</div>;
}

export interface BlockTab<K extends string> {
  key: K;
  label: string;
  count?: number;
  /** Something here needs you. */
  alert?: boolean;
  hidden?: boolean;
}

/**
 * The task's blocks (agreed with Kostya's agent): one open at a time, counts
 * next to the words, a dot where something needs you. On a phone the row
 * scrolls sideways; a fade on the right says there is more.
 */
export function BlockTabs<K extends string>({ tabs, value, onChange, label, id }: {
  tabs: BlockTab<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
  /** Ties the tabs to their panel: `blockPanel(id, value)`. */
  id: string;
}) {
  const shown = tabs.filter((t) => !t.hidden);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // WAI-ARIA tabs: arrows move and open, Home/End jump; Tab leaves the row.
  const onKey = (e: KeyboardEvent, i: number) => {
    const last = shown.length - 1;
    const next = e.key === "ArrowRight" ? (i === last ? 0 : i + 1)
      : e.key === "ArrowLeft" ? (i === 0 ? last : i - 1)
      : e.key === "Home" ? 0 : e.key === "End" ? last : -1;
    if (next < 0) return;
    e.preventDefault();
    onChange(shown[next].key);
    refs.current[next]?.focus();
  };
  // Tabs that do not fit scroll sideways; a faded edge says there is more (PC audit, point 1).
  const row = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const measure = () => setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => { el.removeEventListener("scroll", measure); observer?.disconnect(); };
  }, [shown.length]);
  // The chosen tab slides into view sideways only — never scrolling the page.
  const keys = shown.map((t) => t.key).join("|");
  useEffect(() => {
    const el = row.current;
    const btn = refs.current[keys.split("|").indexOf(value)];
    if (!el || !btn) return;
    const tab = btn.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    if (tab.left < box.left) el.scrollLeft -= box.left - tab.left + 16;
    else if (tab.right > box.right) el.scrollLeft += tab.right - box.right + 16;
  }, [value, keys]);
  const fade = edges.left && edges.right
    ? "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-32px),transparent)]"
    : edges.right ? "[mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)]"
    : edges.left ? "[mask-image:linear-gradient(to_right,transparent,black_24px)]"
    : "";
  return (
    <div
      ref={row}
      role="tablist"
      aria-label={label}
      className={cn("-mx-4 flex gap-5 overflow-x-auto border-b border-p-border px-4 [scrollbar-width:none] lg:mx-0 lg:px-0 [&::-webkit-scrollbar]:hidden", fade)}
    >
      {shown.map((tab, i) => {
        const on = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            id={`${id}-tab-${tab.key}`}
            aria-selected={on}
            aria-controls={`${id}-panel`}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKey(e, i)}
            className={cn(
              "-mb-px inline-flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 text-[15px] font-semibold",
              on ? "border-p-text text-p-text" : "border-transparent text-p-text-muted",
            )}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && <span className="font-medium">{tab.count}</span>}
            {tab.alert && <span aria-label="нужно твоё действие" className="h-2 w-2 rounded-full bg-p-needs" />}
          </button>
        );
      })}
    </div>
  );
}

/** The panel of `BlockTabs` with the same `id`: what the open tab shows. */
export function blockPanel(id: string, value: string) {
  return { role: "tabpanel" as const, id: `${id}-panel`, "aria-labelledby": `${id}-tab-${value}`, tabIndex: 0 };
}

/** A quiet line for an empty place, with what to do next. */
export function EmptyNote({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-card border border-dashed border-p-border-strong px-4 py-4">
      <p className="text-body text-p-text-muted">{children}</p>
      {action}
    </div>
  );
}

const PILL = {
  review: "bg-p-review-tint text-p-review",
  needs: "bg-p-needs-tint text-p-needs",
  blocked: "bg-p-blocked-tint text-p-blocked",
  waiting: "bg-p-waiting-tint text-p-waiting",
  done: "bg-p-done-tint text-p-done",
} as const;

/** A counted status — «1 ждёт проверки»: the Status look with our own words. */
export function Pill({ tone, children }: { tone: keyof typeof PILL; children: ReactNode }) {
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[13px] font-semibold leading-4", PILL[tone])}>
      <span aria-hidden="true" className="h-[7px] w-[7px] shrink-0 rounded-full bg-current" />
      {children}
    </span>
  );
}

/** How much of a project is done: a 6 px bar, the words are next to it. */
export function ProgressLine({ done, total }: { done: number; total: number }) {
  const ratio = total > 0 ? Math.min(1, done / total) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-p-surface-2" aria-hidden="true">
      <div className="h-full rounded-full bg-p-done" style={{ width: `${Math.round(ratio * 100)}%` }} />
    </div>
  );
}

/** An accessible on/off switch: a button with role="switch". */
export function Switch({ on, disabled, label, onChange }: { on: boolean; disabled?: boolean; label: string; onChange?: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-55",
        on ? "bg-p-done" : "bg-p-border-strong",
      )}
    >
      <span className={cn("absolute top-1 h-5 w-5 rounded-full bg-p-surface shadow transition-[left]", on ? "left-6" : "left-1")} />
    </button>
  );
}

/** A filter chip of a list: the word and how many. */
export function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "inline-flex min-h-10 shrink-0 items-center gap-1 rounded-full border px-4 text-[15px] font-semibold",
        on ? "border-p-text bg-p-text text-p-bg" : "border-p-border-strong bg-p-surface text-p-text press-surface",
      )}
    >
      {children}
    </button>
  );
}

/** A caption over a group: 12/16, spaced capitals. */
export function Caption({ children }: { children: ReactNode }) {
  return <h2 className="text-micro font-semibold uppercase tracking-[.08em] text-p-text-muted">{children}</h2>;
}

/** «2 ч назад», «вчера, 15:05» — times read like speech. */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const at = new Date(iso).getTime();
  const min = Math.round((now - at) / 60_000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 6) return `${h} ч назад`;
  const d = new Date(iso);
  const today = new Date(now);
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `сегодня, ${time}`;
  const yesterday = new Date(now - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) return `вчера, ${time}`;
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}
